import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { BackendModuleKey } from "../lib/backendModuleKeys";
import type { ModuleAccessKey, ModuleAccessMarker, PendingRequestRef } from "../lib/accessRequests";
import { normalizeAccessRequestList, resolveAccessLevel } from "../lib/accessRequests";
import { apiHelpers } from "../lib/api";
import type { ModuleGateId } from "../lib/moduleGates";
import { MODULE_GATES } from "../lib/moduleGates";
import { userHasGateGrant } from "../lib/permissions";
import { useAuthStore } from "../stores/authStore";
import { accessRequestQueryKeys } from "./useAccessRequests";

export type ModuleAccessState = {
  granted: boolean;
  pending: PendingRequestRef | null;
  isLoading: boolean;
};

function pendingFromMineList(
  records: ReturnType<typeof normalizeAccessRequestList>,
  module: BackendModuleKey,
): PendingRequestRef | null {
  const match = records.find(
    (r) =>
      r.status === "pending" &&
      r.target_type === "module" &&
      r.module_key === module,
  );
  if (!match) return null;
  return { id: match.id, status: match.status, created_at: match.created_at };
}

export function useGateAccess(
  gateId: ModuleGateId,
  options?: {
    marker?: ModuleAccessMarker | null;
    permissionCodes?: readonly string[];
    apiGrants?: readonly string[] | null;
    fetchPending?: boolean;
  },
): ModuleAccessState {
  const user = useAuthStore((s) => s.user);
  const gate = MODULE_GATES[gateId];
  const marker = options?.marker;
  const fetchPending = options?.fetchPending ?? true;
  const requestModule = gate.accessRequestModule;

  const grantedFromMarker =
    marker !== undefined && marker !== null
      ? resolveAccessLevel(marker) === "full"
      : undefined;

  const grantedFromPermissions = userHasGateGrant(user, gateId, options?.apiGrants);

  const granted =
    grantedFromMarker !== undefined ? grantedFromMarker : grantedFromPermissions;

  const pendingFromMarker = marker?.pending_request ?? null;

  const mineQuery = useQuery({
    queryKey: accessRequestQueryKeys.mine("self"),
    enabled: Boolean(user && fetchPending && !granted && requestModule),
    queryFn: async () => {
      const res = await apiHelpers.accessRequests.list({ scope: "mine" });
      return normalizeAccessRequestList(res.data);
    },
    staleTime: 30_000,
  });

  const pending = useMemo(() => {
    if (!requestModule) return pendingFromMarker;
    if (pendingFromMarker) return pendingFromMarker;
    if (!mineQuery.data) return null;
    return pendingFromMineList(mineQuery.data, requestModule);
  }, [pendingFromMarker, mineQuery.data, requestModule]);

  const isLoading =
    !granted && fetchPending && requestModule != null && mineQuery.isLoading && !pendingFromMarker;

  return { granted, pending, isLoading };
}

/**
 * Resolves module access from DEC-003 markers and/or MODULE_GATES permission grants.
 */
export function useModuleAccess(
  module: ModuleAccessKey,
  options?: {
    marker?: ModuleAccessMarker | null;
    permissionCodes?: readonly string[];
    apiGrants?: readonly string[] | null;
    fetchPending?: boolean;
  },
): ModuleAccessState {
  const gateId = module as ModuleGateId;
  return useGateAccess(gateId, options);
}
